import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import {
  type BulkDeleteImagesFromCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesBulkDeleteData as BulkDeleteData,
  type BulkDeleteImagesFromCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesBulkDeleteError as BulkDeleteError,
  type BulkDeleteImagesFromCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesBulkDeleteResponse as BulkDeleteResponse,
  bulkDeleteImagesFromCollectionV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesBulkDelete as bulkDelete,
} from '../../generated';

export type BulkDeleteCollectionImagesMutationVariables = BulkDeleteData['path'] & BulkDeleteData['body'];

export type BulkDeleteCollectionImagesMutation = BulkDeleteResponse;

/** Why one image was not deleted: `IMAGE_IN_ACTIVE_PDP` or `IMAGE_NOT_FOUND`. */
export type BulkDeleteCollectionImagesFailure = BulkDeleteResponse['failed'][number];

async function bulkDeleteCollectionImages({
  workspace_id,
  collection_id,
  ...body
}: BulkDeleteCollectionImagesMutationVariables) {
  const { data } = await bulkDelete({ path: { workspace_id, collection_id }, body, throwOnError: true });
  return data;
}

/**
 * Deletes many images of a collection in one request. Same as the single delete, images are deleted forever, with
 * their Spark images and PDP links, not only removed from the collection (SDD D55).
 *
 * The result can be partial: images that can't be deleted come back in `failed`, the rest are deleted. A collection
 * out of scope (`404`) or an empty list (`422`) throws and nothing is deleted.
 *
 * @example
 * const { mutateAsync: deleteImages } = useBulkDeleteCollectionImagesMutation();
 * const { deleted_ids, failed } = await deleteImages({ workspace_id, collection_id, image_ids });
 */
export const useBulkDeleteCollectionImagesMutation = <TError = BulkDeleteError, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<
      BulkDeleteCollectionImagesMutation,
      TError,
      BulkDeleteCollectionImagesMutationVariables,
      TContext
    >,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<BulkDeleteCollectionImagesMutation, TError, BulkDeleteCollectionImagesMutationVariables, TContext>(
    {
      mutationKey: ['BulkDeleteCollectionImages'],
      mutationFn: bulkDeleteCollectionImages,
      ...options,
    },
  );
};
